import { isInvoiceOverdue } from "../utils/invoiceDueDate";
import { outstandingInvoices } from "../utils/invoiceOutstanding";
import DashboardCommercialSummary from "../components/DashboardCommercialSummary";
import PageHeading from "../components/PageHeading";
import React from "react";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../axios/AxiosInstance";
import { TrendingUp } from "lucide-react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { loadAtradiusReviews } from "../services/atradiusReview";

const revenueAmount = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value))
    ? new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value))
    : "—";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [nombreFacturesImpayees, setNombreFacturesImpayees] = useState(0);
  const [invoiceError, setInvoiceError] = useState("");
  useEffect(() => {
    let active = true;
    AxiosInstance.get("facture/freelance")
      .then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data)) { setInvoiceError("Impossible de vérifier les factures impayées."); return; }
        setNombreFacturesImpayees(outstandingInvoices(data.filter(invoice => isInvoiceOverdue(invoice))).count);
      })
      .catch(() => {if (active) setInvoiceError("Impossible de vérifier les factures impayées.");});
    return () => {
      active = false;
    };
  }, []);
  const [caMensuel, setCaMensuel] = useState({});
  const monthParts = new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "2-digit",
    timeZone: "Europe/Paris",
  }).formatToParts(new Date());
  const defaultMonth = `${monthParts.find((p) => p.type === "year").value}-${monthParts.find((p) => p.type === "month").value}`;
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [revenueError, setRevenueError] = useState("");
  useEffect(() => {
    let active = true;
    setRevenueLoading(true);
    setRevenueError("");
    setCaMensuel({});
    const [year, month] = selectedMonth.split("-");
    AxiosInstance.get("facture/CA-mois", {
      params: { year: Number(year), month: Number(month) },
    })
      .then(({ data }) => {
        if (!active) return;
        const values = Array.isArray(data) ? data : [data];
        const matching = values.find(
          (row) =>
            row &&
            Number(row.year) === Number(year) &&
            Number(row.month) === Number(month),
        );
        if (matching || selectedMonth === defaultMonth)
          setCaMensuel(matching || values[0] || {});
        else
          setRevenueError(
            "Le serveur ne fournit pas encore de chiffre d’affaires pour le mois sélectionné.",
          );
      })
      .catch(() => {
        if (active)
          setRevenueError(
            "Impossible de charger le chiffre d’affaires de ce mois.",
          );
      })
      .finally(() => {
        if (active) setRevenueLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedMonth, defaultMonth]);
  const [atradius, setAtradius] = useState({
    loading: true,
    reviews: [],
    failures: 0,
  });
  useEffect(() => {
    let active = true;
    loadAtradiusReviews()
      .then((result) => {
        if (active) setAtradius({ ...result, loading: false });
      })
      .catch(() => {
        if (active) setAtradius({ loading: false, reviews: [], failures: 1 });
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header Section */}
        <motion.div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <PageHeading
                icon="dashboard"
                title={<> Bonjour {localStorage.getItem("firstname")} 👋 </>}
                description={
                  <> Voici un aperçu de votre activité aujourd'hui </>
                }
              />
            </div>
          </div>
        </motion.div>
        {invoiceError && <p role="status" className="mb-4 text-sm text-amber-700">{invoiceError}</p>}
        {Number(nombreFacturesImpayees) > 0 && (
          <div
            role="status"
            className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-5"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="font-semibold text-rose-900">
                  {nombreFacturesImpayees} facture
                  {Number(nombreFacturesImpayees) > 1 ? "s" : ""} impayée
                  {Number(nombreFacturesImpayees) > 1 ? "s" : ""}
                </h2>
                <p className="mt-1 text-sm text-rose-800">
                  Des paiements sont en attente. Consultez les factures pour
                  suivre les règlements et préparer les relances.
                </p>
              </div>
              <button
                onClick={() => navigate("/facture")}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              >
                Voir les factures
              </button>
            </div>
          </div>
        )}
        {!atradius.loading &&
          (atradius.reviews.length > 0 || atradius.failures > 0) && (
            <div
              role="status"
              className="mb-6 rounded-xl border border-orange-200 bg-orange-50 p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-semibold text-orange-900">
                    {atradius.reviews.length
                      ? `Atradius à revoir : ${atradius.reviews.length} client${atradius.reviews.length > 1 ? "s" : ""}`
                      : "Vérification Atradius incomplète"}
                  </h2>
                  {atradius.reviews.length > 0 && (
                    <>
                      <p className="mt-1 text-sm text-orange-800">
                        Le montant en cours dépasse la couverture accordée.
                      </p>
                      <p className="mt-2 text-sm font-medium text-orange-900">
                        {atradius.reviews
                          .slice(0, 3)
                          .map(
                            ({ client }) =>
                              client.companyName || client.denomination,
                          )
                          .join(" · ")}
                        {atradius.reviews.length > 3 ? "…" : ""}
                      </p>
                    </>
                  )}
                  {atradius.failures > 0 && (
                    <p className="mt-2 text-xs text-orange-800">
                      Certaines couvertures n’ont pas pu être vérifiées.
                    </p>
                  )}
                </div>
                <button
                  onClick={() => navigate("/atradius")}
                  className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
                >
                  Voir Atradius
                </button>
              </div>
            </div>
          )}
        <section
          aria-labelledby="monthly-revenue-title"
          className="mb-8 rounded-2xl border border-slate-200 bg-white p-6"
        >
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-emerald-50 p-3 text-emerald-600">
                <TrendingUp size={22} />
              </div>
              <div>
                <h2
                  id="monthly-revenue-title"
                  className="text-lg font-semibold text-slate-900"
                >
                  Chiffre d’affaires mensuel HT
                </h2>
                <p className="mt-1 text-sm capitalize text-slate-500">
                  {new Date(`${selectedMonth}-01T12:00:00`).toLocaleDateString(
                    "fr-FR",
                    { month: "long", year: "numeric" },
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-5">
              <div>
                <label
                  htmlFor="revenue-month"
                  className="mb-1 block text-xs font-medium text-slate-500"
                >
                  Choisir le mois
                </label>
                <input
                  id="revenue-month"
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => {
                    if (/^\d{4}-\d{2}$/.test(event.target.value))
                      setSelectedMonth(event.target.value);
                  }}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Total HT du mois</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">
                  {revenueLoading
                    ? "…"
                    : revenueAmount(caMensuel.currentMonthTotalHT)}
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { key: "cdi", label: "CDI" },
              { key: "cdd", label: "CDD" },
              { key: "vacation", label: "Vacation" },
              { key: "interim", label: "Intérim" },
            ].map((category) => (
              <div
                key={category.key}
                className="rounded-xl border border-slate-100 bg-slate-50 p-4"
              >
                <p className="text-sm font-medium text-slate-600">
                  {category.label}
                </p>
                <p className="mt-2 text-xl font-semibold text-slate-900">
                  {revenueLoading
                    ? "…"
                    : revenueAmount(caMensuel.breakdownHT?.[category.key])}
                </p>
                <p className="mt-1 text-xs text-slate-400">Hors taxes</p>
              </div>
            ))}
          </div>
          {revenueError && (
            <p role="status" className="mt-4 text-sm text-amber-700">
              {revenueError}
            </p>
          )}
          {!revenueLoading && !revenueError && !caMensuel.breakdownHT && (
            <p className="mt-4 text-xs text-slate-500">
              La répartition HT par type de contrat n’est pas encore fournie par
              le serveur.
            </p>
          )}
        </section>
        <DashboardCommercialSummary />
      </div>
    </div>
  );
}
